// Identity Module Types
import type { Pet } from '../pets';
import type { Unit } from '../residence';

export enum UserRole {
  ADMIN = 'ADMIN',
  /** Admin acotado a una sección (§25). Su perímetro lo impone el backend. */
  SECTION_ADMIN = 'SECTION_ADMIN',
  RESIDENT = 'RESIDENT',
  GUARD = 'GUARD',
  // Roles del backend
  TENANT_USER = 'TENANT_USER',
}

export type TenantRole = 'ADMIN' | 'SECTION_ADMIN' | 'RESIDENT' | 'GUARD' | 'STAFF' | 'FAMILIAR';

// Relación usuario-unidad (un usuario puede tener múltiples unidades)
export interface UserUnit {
  id: string;
  unitId: string;
  isOwner: boolean;
  isPrimary: boolean;
  /** True si el usuario es el residente principal de esta unidad específica. */
  isPrincipal?: boolean;
  moveInDate?: string | null;
  moveOutDate?: string | null;
  unit: Unit;
}

// Legacy - mantener para compatibilidad
export interface ResidenceInfo {
  tower: string;
  unit: string;
  street?: string;
}

export interface FiscalData {
  rfc: string;
  businessName: string;
  taxRegime: string;
  zipCode: string;
  address: string;
  email: string;
}

export type HouseholdMemberRelation = 'SPOUSE' | 'CHILD' | 'PARENT' | 'OTHER';
export type HouseholdMemberAccountStatus =
  | 'PENDING_VERIFICATION'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'BLOCKED'
  | 'UNKNOWN';

export interface HouseholdMember {
  id: string;
  name: string;
  relation: HouseholdMemberRelation;
  email: string; // Requerido para crear cuenta
  age?: number;
  phone?: string;
  accountStatus: HouseholdMemberAccountStatus;
  /**
   * True when this entry is backed by a real household_member record and can be
   * edited/deleted. False for co-residents surfaced from tenant_members (their id
   * is not resolvable by the household-members endpoints). Defaults to true when
   * the backend omits it (older API).
   */
  editable?: boolean;
  /** El backend marca con is_principal al residente principal de la unidad. */
  isPrincipal?: boolean;
  unitId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface User {
  id: string;
  /** Id del miembro del tenant (tenant_members.id). Presente en usuarios admin/equipo; usado para asignar admin de sección. */
  memberId?: string | null;
  name: string;
  email: string;
  phone?: string;
  role: UserRole | string;
  tenantRole?: TenantRole | string;
  abilities?: Ability[];
  avatarUrl?: string;
  avatarFileId?: string;
  pin?: string;
  acsCode?: string | null;
  /**
   * El backend marca el softdelete devolviendo status: 'DELETED' (no hay campo
   * deletedAt separado en /users). PENDING_VERIFICATION es un residente/familiar
   * invitado que todavía no acepta la invitación (magic link) — no está bloqueado.
   */
  status: 'ACTIVE' | 'BLOCKED' | 'DELETED' | 'PENDING_VERIFICATION';
  // Nueva estructura: múltiples unidades
  units?: UserUnit[];
  // Legacy - mantener para compatibilidad
  residenceInfo?: ResidenceInfo;
  fiscalData?: FiscalData;
  householdMembers?: HouseholdMember[];
  pets?: Pet[];
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
  /** Si es un familiar, el titular al que pertenece (resuelto por el backend desde tenant_members.related_to_member_id). Null si no aplica. */
  relatedTo?: { masterUserId: string; name: string } | null;
  /** True si es el residente principal de su unidad (tenant_members.is_principal). */
  isPrincipal?: boolean;
  /** False para cuentas invitadas por magic link que nunca asignaron su propia contraseña (master_users.password_set_by_user). */
  passwordSetByUser?: boolean;
}

// Multi-tenant support
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'PENDING';
  address?: string;
  phone?: string;
  email?: string;
}

// TenantBranding is exported from admin module
// Re-export or import from there if needed

export interface TenantWithBranding extends Tenant {
  branding?: {
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    faviconUrl?: string;
  };
  features?: Record<string, boolean>;
  config?: Record<string, string>;
}

// Selección de tenant para usuarios con múltiples tenants
export interface TenantSelection {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  role: TenantRole;
  status: string;
  logoUrl?: string;
  /**
   * MasterUser dueño de esta membresía. Mandar tal cual a selectTenant junto
   * con tenantId — el mismo email puede resolver a cuentas MasterUser
   * distintas en tenants distintos, no solo a membresías de una sola cuenta.
   */
  userId: string;
}

// Abilities/Permissions del usuario
export interface Ability {
  action: string;
  subject: string;
}

// Respuesta de autenticación con magic link
export interface AuthTokens {
  accessToken: string;
  refreshToken?: string; // Ausente en clientes web: viaja en cookie HttpOnly, no en el body.
  csrfToken?: string; // Solo presente en clientes web.
  expiresIn: number;
}

export interface MagicLinkVerifyResponse {
  user: User;
  tokens: AuthTokens | null;
  tenant: TenantWithBranding | null;
  requiresTenantSelection: boolean;
  tenants?: TenantSelection[];
  abilities: Ability[];
}

// Request para crear miembro del hogar
export interface CreateHouseholdMemberRequest {
  name: string;
  relation: HouseholdMemberRelation;
  email: string;
  age?: number;
  phone?: string;
}

// Role and Permissions System
export type PermissionAction = 'create' | 'read' | 'update' | 'delete' | 'manage';

// Grouped format (for internal UI use)
export interface RolePermission {
  subject: string;     // 'User', 'Amenity', 'Payment', etc.
  actions: PermissionAction[];
}

// Flat format (what the backend API returns/expects)
export interface FlatPermission {
  action: string;
  subject: string;
  conditions?: Record<string, unknown>;
  id?: string;
  createdAt?: string;
}

export interface Role {
  id: string;
  name: string;
  displayName?: string;
  description?: string;
  isSystem: boolean;   // true = predefined role (ADMIN, GUARD), not editable
  permissions: (RolePermission | FlatPermission)[];
  createdAt: string;
  updatedAt?: string;
}

// For creating/updating roles
export interface CreateRoleRequest {
  name: string;
  displayName?: string;
  description?: string;
  permissions: FlatPermission[];
}

export interface UpdateRoleRequest {
  name?: string;
  displayName?: string;
  description?: string;
  permissions?: FlatPermission[];
}
